// =============================================
// Every pop-up window can be moved by its title bar (sitewide rule, owner 2026-10-19).
// Installed once (App.tsx), so no pop-up needs code of its own — current or future.
//
// A press counts as "on a pop-up's title bar" when:
//   - it is inside a pop-up box: a [role="dialog"] element, or the child of a full-
//     screen fixed overlay (the dimmed background every modal here uses), or a fixed
//     box of its own;
//   - and inside that box's title bar: its first child when that is a header (a
//     heading in it, or a class with "head"/"title"), else the row holding its first
//     heading;
//   - and not on a button / field / link (those keep working as before).
// The box is moved with the CSS `translate` property, which is separate from
// `transform`, so a pop-up's own transform (or the older useDragOffset hook) is never
// overwritten. A pop-up opened again is a new element, so it starts centred again.
// Opt out with data-no-drag on the box; full-screen sheets are never moved.
// Listens in the CAPTURE phase: most pop-up boxes stop mousedown from bubbling (so a
// press inside does not close them through the overlay).
// =============================================

const INTERACTIVE = 'button, input, select, textarea, a, label, [role="button"], [role="combobox"], [role="listbox"], [role="option"], [contenteditable="true"], .sd-wrapper';
const HEADER_LIKE = 'header, h1, h2, h3, h4, [class*="head"], [class*="title"]';
const MIN_VISIBLE = 60; // px of the title bar that always stays on screen

const offsets = new WeakMap<HTMLElement, { x: number; y: number }>();

function coversViewport(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  return r.width >= window.innerWidth * 0.9 && r.height >= window.innerHeight * 0.9;
}

/** The pop-up box the element is in, or null. */
export function findPopupBox(start: HTMLElement): HTMLElement | null {
  for (let el: HTMLElement | null = start; el && el !== document.body; el = el.parentElement) {
    if (el.hasAttribute('data-no-drag')) return null;
    const parent = el.parentElement;
    const isDialog = el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog';
    const parentIsOverlay = !!parent && parent !== document.body
      && getComputedStyle(parent).position === 'fixed' && coversViewport(parent);
    const isFixedBox = getComputedStyle(el).position === 'fixed' && !coversViewport(el);
    if (isDialog || parentIsOverlay || isFixedBox) {
      if (coversViewport(el)) {
        // a full-screen dialog wrapper (overlay with role=dialog): the box is further in
        if (isDialog && !parentIsOverlay) continue;
        return null;
      }
      return el;
    }
  }
  return null;
}

/** The title bar of a pop-up box, or null when it has none. */
export function findTitleBar(box: HTMLElement): HTMLElement | null {
  const first = box.firstElementChild as HTMLElement | null;
  if (first && (first.matches(HEADER_LIKE) || first.querySelector('h1, h2, h3, h4'))) return first;
  const h = box.querySelector('h1, h2, h3, h4') as HTMLElement | null;
  if (!h) return null;
  return h.parentElement && h.parentElement !== box ? h.parentElement : h;
}

/** The box to move when the press is on its title bar, or null. */
function boxForPress(target: HTMLElement): HTMLElement | null {
  if (target.closest(INTERACTIVE)) return null;
  if (target.closest('.pr-modal-drag')) return null; // moved by useDragOffset already
  const box = findPopupBox(target);
  if (!box) return null;
  const bar = findTitleBar(box);
  return bar && bar.contains(target) ? box : null;
}

function onMouseDown(e: MouseEvent) {
  if (e.button !== 0 || !(e.target instanceof HTMLElement)) return;
  const box = boxForPress(e.target);
  if (!box) return;
  e.preventDefault(); // no text selection while dragging
  const start = offsets.get(box) || { x: 0, y: 0 };
  const rect = box.getBoundingClientRect();
  const sx = e.clientX, sy = e.clientY;
  const prevSelect = document.body.style.userSelect;
  document.body.style.userSelect = 'none';
  const move = (ev: MouseEvent) => {
    let dx = ev.clientX - sx, dy = ev.clientY - sy;
    // sideways: part of the title bar stays on screen; up / down: the whole box stays
    // on screen, so its Save / Cancel row at the bottom can always be reached
    dx = Math.min(Math.max(dx, MIN_VISIBLE - rect.right), window.innerWidth - MIN_VISIBLE - rect.left);
    dy = Math.min(Math.max(dy, Math.min(-rect.top, 0)), Math.max(window.innerHeight - rect.bottom, 0));
    const next = { x: start.x + dx, y: start.y + dy };
    offsets.set(box, next);
    box.style.translate = `${next.x}px ${next.y}px`;
  };
  const up = () => {
    window.removeEventListener('mousemove', move);
    window.removeEventListener('mouseup', up);
    document.body.style.userSelect = prevSelect;
  };
  window.addEventListener('mousemove', move);
  window.addEventListener('mouseup', up);
}

// Move cursor + "Drag to move" tip on a title bar, set the first time the mouse is over it.
function onMouseOver(e: MouseEvent) {
  if (!(e.target instanceof HTMLElement)) return;
  const box = findPopupBox(e.target);
  if (!box || box.dataset.dragReady) return;
  box.dataset.dragReady = '1';
  const bar = findTitleBar(box);
  if (!bar || bar.closest('.pr-modal-drag')) return;
  bar.style.cursor = 'move';
  if (!bar.title) bar.title = 'Drag to move';
}

/** Turn pop-up dragging on for the whole app; returns the undo. */
export function installPopupDrag(): () => void {
  window.addEventListener('mousedown', onMouseDown, true);
  window.addEventListener('mouseover', onMouseOver, true);
  return () => {
    window.removeEventListener('mousedown', onMouseDown, true);
    window.removeEventListener('mouseover', onMouseOver, true);
  };
}
